/**
 * Camada das estações de radiossonda no mapa.
 *
 * Duas camadas, de propósito:
 *
 * - `layer`: o MARCO de cada estação (base, mastro e seta), que muda pouco —
 *   o balão sobe 2× por dia, então a reavaliação só repinta quando alguma
 *   estação troca de estado;
 * - `voo`: a trilha e o BALÃO, que andam a cada quadro.
 *
 * A estação NÃO é desenhada como balão. Quando era, o mapa ficava com dois
 * balões por estação — um preso no chão e outro solto na trilha —, e nada
 * dizia qual era qual. Só o que voou tem forma de balão.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import type { FeatureLike } from 'ol/Feature';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { useTimeStore } from '../state/time';
import { WYOMING_ATTRIBUTION } from './api';
import { MARCO_EXTENT, W_BASE, W_SETA, marcoPath } from './marco';
import { lonLatToMap } from '../map/projection';
import {
  corAltura,
  corCape,
  estadoDe,
  perfilNoInstante,
  pontoDeLancamento,
  posicaoNoInstante,
  type EstadoSondagem,
} from './perfil';
import { useSoundingStore } from './store';
import type { EstacaoSondagem, Perfil } from './types';

export interface SondagemFeature {
  estacao: EstacaoSondagem;
  perfil: Perfil | undefined;
  estado: EstadoSondagem;
}

const CONTORNO = 'rgba(255,255,255,0.8)';
const COR_ANTIGA = '#9fb4c7';
const COR_AUSENTE = '#7d8896';
const COR_ERRO = '#e0a23c';

interface Aparencia {
  raio: number;
  preenchimento: string | null;
  contorno: string;
  tracejado: number[];
  opacidade: number;
}

/**
 * Aparência conforme o estado. Preenchido só quando a sondagem vale para o
 * instante exibido; vazado quando é velha; tracejado quando não há.
 */
export function aparenciaDe(f: SondagemFeature, tamanho: number): Aparencia {
  const cape = f.perfil?.indices?.MUCAPE;
  switch (f.estado) {
    case 'recente':
      return { raio: tamanho, preenchimento: corCape(cape), contorno: CONTORNO, tracejado: [], opacidade: 0.95 };
    case 'antiga':
      return { raio: tamanho * 0.8, preenchimento: null, contorno: COR_ANTIGA, tracejado: [], opacidade: 0.8 };
    case 'erro':
      return { raio: tamanho * 0.8, preenchimento: null, contorno: COR_ERRO, tracejado: [2, 1.5], opacidade: 0.85 };
    default:
      return { raio: tamanho * 0.75, preenchimento: null, contorno: COR_AUSENTE, tracejado: [1.5, 1.5], opacidade: 0.6 };
  }
}

// Os símbolos crescem com o zoom pela regra comum a todas as camadas
// (`map/escalaDoZoom.ts`). A radiossonda tinha uma curva própria, que ia de
// 0,8 a 2,4 e saturava no nível 6: o marco chegava a 40 px e o anel do balão a
// 58 px — grandes demais.


/**
 * Piso da radiossonda: o fator dela nunca fica abaixo de 0,9.
 *
 * São 14 estações, e o marco é só traço — sem área pintada. Com o 0,7 comum,
 * de longe ele ficava com 13 px e sumia entre hexágonos e gotas coloridas. Com
 * 0,9 o marco tem pelo menos 16,6 px no tamanho padrão (2,1 × 8 × 0,9, mais o
 * traço). O piso é do FATOR, e não um número de px: o controle "Tamanho" do
 * painel continua multiplicando (no tamanho 4 o piso dá 9 px, no 14, 28 px).
 * Vale para o marco, a trilha e o balão — a trilha nasce no marco e o balão
 * anda nela, e com fatores diferentes eles mudariam de proporção entre si de
 * longe. Do nível 5 para dentro nada muda: ali o fator comum já é 0,9 ou mais.
 */
export const FATOR_MIN_DA_SONDAGEM = 0.9;

export function fatorDaSondagem(resolution: number | undefined): number {
  return Math.max(FATOR_MIN_DA_SONDAGEM, fatorDoZoom(resolution));
}

/** Tamanho efetivo (o do painel vezes o fator) na resolução dada. */
export function tamanhoNoZoom(tamanho: number, resolution: number | undefined): number {
  return tamanho * fatorDaSondagem(resolution);
}

const sprites = criarCache<HTMLCanvasElement>('sondagem.sprites', 200);
const estilos = criarCache<Style>('sondagem.estilos', 200);

function chave(a: Aparencia): string {
  return `${dpr()}|${a.raio.toFixed(2)}|${a.preenchimento ?? '-'}|${a.contorno}|${a.tracejado.join(',')}|${a.opacidade}`;
}

/** Folga em px para o contorno não ser cortado na borda do sprite. */
const FOLGA_SPRITE = 2;

/**
 * Caixa do sprite do marco, em px de CSS.
 *
 * O desenho tem a origem na BASE e sobe: ocupa de `-MARCO_EXTENT·raio` até 0
 * em y, e ±`W_BASE·raio` em x. A caixa é justa em volta disso, mais folga —
 * um sprite quadrado centrado na origem desperdiçaria metade da altura.
 */
export function caixaSprite(raio: number): { largura: number; altura: number; baseY: number } {
  const meiaLargura = Math.max(W_BASE, W_SETA) * raio + FOLGA_SPRITE;
  const altura = MARCO_EXTENT * raio + 2 * FOLGA_SPRITE;
  return { largura: meiaLargura * 2, altura, baseY: altura - FOLGA_SPRITE };
}

/**
 * Âncora vertical: a BASE do mastro, que é o ponto da estação.
 *
 * O marco cresce para cima a partir do solo, então a base tem que cair na
 * coordenada real — é dali que a trilha nasce. A primeira versão reaproveitou
 * a fórmula do símbolo anterior sem reajustar para esta geometria, e o marco
 * saía ~13 px ACIMA da estação: a trilha parecia não começar nele.
 */
export function ancoraY(raio: number): number {
  const { altura, baseY } = caixaSprite(raio);
  return baseY / altura;
}

function sprite(a: Aparencia, k: string): HTMLCanvasElement | null {
  const pronto = sprites.get(k);
  if (pronto) return pronto;

  const escala = dpr();
  const { largura, altura, baseY } = caixaSprite(a.raio);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(4, Math.ceil(largura * escala));
  canvas.height = Math.max(4, Math.ceil(altura * escala));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(escala, escala);
  ctx.translate(largura / 2, baseY);
  ctx.scale(a.raio, a.raio);

  // O marco é só traço: preencher não faz sentido num mastro, então a cor do
  // CAPE vai no próprio traço quando há sondagem recente.
  ctx.strokeStyle = a.preenchimento ?? a.contorno;
  ctx.lineWidth = (a.preenchimento ? 1.5 : 1.1) / a.raio;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (a.tracejado.length) ctx.setLineDash(a.tracejado.map((d) => d / a.raio));
  ctx.stroke(marcoPath());

  sprites.set(k, canvas);
  return canvas;
}

export function estiloDe(f: SondagemFeature, tamanho: number): Style | undefined {
  const a = aparenciaDe(f, tamanho);
  const k = chave(a);
  let s = estilos.get(k);
  if (s) return s;
  const img = sprite(a, k);
  if (!img) return undefined;
  s = new Style({
    image: new Icon({
      img,
      anchor: [0.5, ancoraY(a.raio)],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      opacity: a.opacidade,
      scale: 1 / dpr(),
    }),
  });
  estilos.set(k, s);
  return s;
}


const estilosDeTrilha = criarCache<Style[]>('sondagem.trilha', 300);

/** Um segmento da trilha: o traço engrossa com o símbolo, mais devagar que ele. */
/** Largura do traço colorido da trilha, em px. */
export function larguraDaTrilha(tamanho: number, fator: number): number {
  return Math.round(2.2 * (tamanho / 8) * Math.sqrt(fator) * 10) / 10;
}

function estiloDaTrilha(cor: string, tamanho: number, fator: number): Style[] {
  const largura = larguraDaTrilha(tamanho, fator);
  const k = `${cor}|${largura}`;
  let s = estilosDeTrilha.get(k);
  if (!s) {
    s = [
      new Style({ stroke: new Stroke({ color: 'rgba(8,16,24,0.6)', width: largura + 1.8, lineCap: 'round' }) }),
      new Style({ stroke: new Stroke({ color: cor, width: largura, lineCap: 'round' }) }),
    ];
    estilosDeTrilha.set(k, s);
  }
  return s;
}

const estilosDeBalao = criarCache<Style[]>('sondagem.balao', 300);

/** Raios do balão, em múltiplos do tamanho (exportados para a tabela de tamanhos e os testes). */
export const BALAO = {
  /** Anel do balão em voo. Era 1,5: com o tamanho 8 dava 58 px de diâmetro de perto. */
  anel: 1.2,
  /** Ponto do balão em voo. */
  ponto: 0.55,
  /** Balão parado no fim da trilha. */
  parado: 0.7,
} as const;

/**
 * O balão. Subindo: anel em volta, para o olho achá-lo no meio da trilha.
 * Parado no fim: só o ponto com anel claro — um ponto da cor da altura se
 * perderia no fim da trilha, que tem exatamente essa cor.
 */
function estiloDoBalao(subindo: boolean, cor: string, tamanho: number): Style[] {
  const k = `${subindo ? 's' : 'f'}|${cor}|${tamanho.toFixed(1)}`;
  let s = estilosDeBalao.get(k);
  if (s) return s;
  s = subindo
    ? [
        new Style({ image: new CircleStyle({ radius: tamanho * BALAO.anel, stroke: new Stroke({ color: 'rgba(127,227,255,0.6)', width: 1.4 }) }) }),
        new Style({
          image: new CircleStyle({ radius: tamanho * BALAO.ponto, fill: new Fill({ color: cor }), stroke: new Stroke({ color: 'rgba(8,16,24,0.85)', width: 1.2 }) }),
        }),
      ]
    : [
        new Style({
          image: new CircleStyle({ radius: tamanho * BALAO.parado, fill: new Fill({ color: cor }), stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 1.6 }) }),
        }),
        new Style({ image: new CircleStyle({ radius: tamanho * BALAO.parado, stroke: new Stroke({ color: 'rgba(8,16,24,0.7)', width: 0.8 }) }) }),
      ];
  estilosDeBalao.set(k, s);
  return s;
}

export interface SoundingLayer {
  layer: VectorLayer<VectorSource>;
  voo: VectorLayer<VectorSource>;
  dispose: () => void;
}

export function createSoundingLayer(renderPopup: (f: SondagemFeature) => ReactNode | null): SoundingLayer {
  const source = new VectorSource({ attributions: WYOMING_ATTRIBUTION });

  /**
   * Camada do VOO: a trilha percorrida e o balão no instante exibido.
   *
   * Separada da dos marcadores porque muda a cada quadro, enquanto a outra
   * fica quase sempre parada — misturá-las obrigaria a repintar as 14
   * estações a cada avanço do loop.
   */
  const fonteVoo = new VectorSource();
  const voo = new VectorLayer({
    source: fonteVoo,
    zIndex: Z.FIRES + 2,
    className: 'ol-layer amaview-sounding-voo',
    properties: { id: 'sounding-voo', title: 'Voo da radiossonda' },
  });
  const layer = new VectorLayer({
    source,
    // Acima dos focos: são poucos pontos e não escondem nada, e ficar embaixo
    // os tornaria invisíveis nas regiões com muita queimada.
    zIndex: Z.FIRES + 1,
    className: 'ol-layer amaview-sounding',
    properties: { id: 'sounding', title: 'Radiossonda' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  layer.setStyle((feature, resolution) => {
    const d = feature.get('sondagem') as SondagemFeature | undefined;
    if (!d) return undefined;
    // Com trilha, o marco é redundante: o começo do percurso JÁ mostra de
    // onde o balão saiu, e desenhar os dois fazia parecer que havia dois
    // objetos para a mesma sonda. Sem trilha, ele é a única marca da estação.
    if (d.perfil?.trilha?.length) return undefined;
    const tamanho = tamanhoNoZoom(useSoundingStore.getState().tamanho, resolution);
    const s = estiloDe(d, tamanho);
    if (!s) return undefined;
    // Sobre a foto de satélite do fundo, o selo escuro no meio do mastro (ele sai da base e sobe).
    const meio = (MARCO_EXTENT * aparenciaDe(d, tamanho).raio) / 2;
    return comSelo([s], meio + 3, resolution, meio);
  });

  layer.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('sondagem') as SondagemFeature | undefined;
    return d ? renderPopup(d) : null;
  });

  let desenhadas: readonly number[] = [];

  function reconstruir(): void {
    const { dados } = useSoundingStore.getState();
    source.clear(true);
    source.addFeatures(
      dados.estacoes.map((e) => {
        const f = new Feature({ geometry: new Point([e.mx, e.my]) });
        f.set('sondagem', { estacao: e, perfil: undefined, estado: 'ausente' } satisfies SondagemFeature);
        return f;
      }),
    );
    desenhadas = dados.estacoes.map((e) => e.wmo);
    reavaliar();
  }

  function reavaliar(): void {
    const { dados, visivel } = useSoundingStore.getState();
    layer.setVisible(visivel);
    if (!visivel) return;

    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;

    let mudou = false;
    for (const f of source.getFeatures()) {
      const d = f.get('sondagem') as SondagemFeature;
      const perfil = perfilNoInstante(dados.perfisDe.get(d.estacao.wmo), instante);
      const estado = estadoDe(perfil, instante);
      if (estado === d.estado && perfil === d.perfil) continue;

      // O marco vai para onde o balão DE FATO saiu do solo, e não para a
      // coordenada cadastrada: a lista do IGRA é arredondada e em São Gabriel
      // da Cachoeira erra 10,7 km (Santarém 9,1; Porto Velho 6,4). Desenhado
      // no cadastro, o marco ficava visivelmente fora da própria trilha e
      // parecia um segundo objeto solto.
      const lancamento = pontoDeLancamento(perfil);
      const geom = f.getGeometry() as Point | undefined;
      if (geom) {
        const [x, y] = lancamento
          ? lonLatToMap(lancamento.lon, lancamento.lat)
          : [d.estacao.mx, d.estacao.my];
        if (Number.isFinite(x) && Number.isFinite(y)) geom.setCoordinates([x, y]);
      }

      f.set('sondagem', { estacao: d.estacao, perfil, estado } satisfies SondagemFeature, true);
      mudou = true;
    }
    if (mudou) layer.changed();
  }

  /**
   * Desenha onde o balão está no instante exibido, e o caminho que ele já
   * percorreu até ali.
   *
   * Fora da janela de voo não há nada: o balão não estava em lugar nenhum
   * antes de subir nem depois de estourar. É isso que faz o marcador aparecer
   * e sumir conforme o loop anda para a frente e para trás.
   */
  /**
   * Assinatura do que está desenhado. Em ~99% dos quadros nada muda (as
   * estações estão em 'final' ou 'antes'), e redesenhar ~600 features com
   * 2.400 objetos de estilo a 5 fps era desperdício puro.
   */
  let assinaturaVoo = '';

  function desenharVoo(): void {
    const { dados, visivel, tamanho } = useSoundingStore.getState();
    voo.setVisible(visivel);
    if (!visivel) {
      if (assinaturaVoo !== '') {
        fonteVoo.clear(true);
        assinaturaVoo = '';
      }
      return;
    }

    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;

    // A fase e a altura do balão resumem tudo o que o desenho depende.
    const assinatura = dados.estacoes
      .map((e) => {
        const perfil = perfilNoInstante(dados.perfisDe.get(e.wmo), instante);
        const pos = posicaoNoInstante(perfil, instante);
        return `${e.wmo}:${estadoDe(perfil, instante)}:${pos ? `${pos.fase}:${pos.z.toFixed(0)}` : '-'}`;
      })
      .join('|') + `|${tamanho}`;
    if (assinatura === assinaturaVoo) return;
    assinaturaVoo = assinatura;

    fonteVoo.clear(true);

    for (const e of dados.estacoes) {
      const perfil = perfilNoInstante(dados.perfisDe.get(e.wmo), instante);
      if (!perfil?.trilha?.length) continue;
      // A trilha acompanha o marcador: some quando a sondagem some.
      const estado = estadoDe(perfil, instante);
      if (estado === 'ausente' || estado === 'erro') continue;

      const pos = posicaoNoInstante(perfil, instante);
      // A trilha para onde o balão está. Antes do lançamento (`pos` nulo) o
      // percurso inteiro aparece mesmo assim: "por onde a sonda passou" vale o
      // tempo todo, e a janela de voo é de 86 min num loop de 24 h.
      //
      // O corte é pelo TEMPO, e não pela altura, para casar exatamente com a
      // interpolação de `posicaoNoInstante` — cortar por `z` dependia de a
      // subida ser monotônica, que é verdade hoje e não é garantia.
      const ateSeg = pos && pos.fase === 'subindo' ? (instante! - perfil.ms!) / 1000 : Infinity;
      const pts = perfil.trilha.filter((t) => t.t === null || t.t <= ateSeg);
      if (pos && pos.fase === 'subindo') pts.push({ x: pos.lon, y: pos.lat, z: pos.z, t: null });

      // Um segmento por par de pontos, colorido pela altura — assim a subida
      // se lê ao longo do traço, sem precisar de legenda no mapa.
      for (let i = 1; i < pts.length; i++) {
        const a = lonLatToMap(pts[i - 1]!.x, pts[i - 1]!.y);
        const b = lonLatToMap(pts[i]!.x, pts[i]!.y);
        if (![...a, ...b].every(Number.isFinite)) continue;
        const seg = new Feature({ geometry: new LineString([a, b]) });
        seg.set('voo', { estacao: e, perfil });
        const cor = corAltura(pts[i]!.z);
        seg.setStyle((_, resolution) => estiloDaTrilha(cor, tamanho, fatorDaSondagem(resolution)));
        fonteVoo.addFeature(seg);
      }

      if (!pos) continue;
      const [bx, by] = lonLatToMap(pos.lon, pos.lat);
      if (!Number.isFinite(bx) || !Number.isFinite(by)) continue;
      const balao = new Feature({ geometry: new Point([bx, by]) });
      balao.set('voo', { estacao: e, perfil });
      // O balão em voo fica quase em cima do marcador da estação: a deriva
      // real é de ~20 km, uns 5 px na escala da Amazônia. O anel externo é o
      // que separa os dois, sem exagerar a distância.
      // Subindo: anel pulsante em volta, para o olho achá-lo no meio da
      // trilha. Parado no fim: só o ponto, para não competir com o balão em
      // voo das outras estações nem sugerir movimento onde não há.
      const subindo = pos.fase === 'subindo';
      const corDoBalao = corAltura(pos.z);
      balao.setStyle((_, resolution) => estiloDoBalao(subindo, corDoBalao, tamanhoNoZoom(tamanho, resolution)));
      fonteVoo.addFeature(balao);
    }
  }

  voo.set('popup', (feature: FeatureLike): ReactNode | null => {
    const v = feature.get('voo') as { estacao: EstacaoSondagem; perfil: Perfil } | undefined;
    if (!v) return null;
    // O estado é recalculado, e não fixado em 'recente': clicar na trilha de
    // uma sondagem de ontem escondia o aviso "anterior ao período exibido"
    // que o marcador, 5 px ao lado, mostrava.
    const { displayedTime, currentTime } = useTimeStore.getState();
    const estado = estadoDe(v.perfil, displayedTime ?? currentTime);
    return renderPopup({ estacao: v.estacao, perfil: v.perfil, estado });
  });

  const pararStore = useSoundingStore.subscribe((a, b) => {
    if (a.dados !== b.dados) {
      const novas = a.dados.estacoes.map((e) => e.wmo);
      const trocou = novas.length !== desenhadas.length || novas.some((w, i) => w !== desenhadas[i]);
      if (trocou) reconstruir();
      else reavaliar();
      desenharVoo();
      return;
    }
    if (a.visivel !== b.visivel) {
      reavaliar();
      desenharVoo();
    } else if (a.tamanho !== b.tamanho) {
      layer.changed();
      desenharVoo();
    }
  });

  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (a.displayedTime !== b.displayedTime || a.currentTime !== b.currentTime) {
      reavaliar();
      desenharVoo();
    }
  });

  reconstruir();
  desenharVoo();

  return {
    layer,
    voo,
    dispose: () => {
      pararStore();
      pararTempo();
      source.clear(true);
      fonteVoo.clear(true);
    },
  };
}
